/**
 * PartnerLoginSheet — the person who signs in for a partner, and signs on their behalf.
 *
 * WHY AN ADMIN CREATES THIS AT ALL. A partner reaches fleet-partner by one-time code, and
 * `link_auth_profile` binds that session to a `profiles` row it finds by phone or email
 * (`20260725180000`, `link_auth_profile`). So the row has to exist BEFORE the partner first signs
 * in, and only an admin can write it: `profiles_insert` is `WITH CHECK (is_admin())`. [D-B48]'s
 * backfill runs straight into this — partner 2 holds ten of the twelve live third-party contracts
 * and has never had a login, and nobody can sign a Vehicle Schedule in an app they cannot enter.
 *
 * THE ROW IS A RESERVATION, NOT AN ACCOUNT. `user_id` stays NULL until the partner's first code
 * lands; there is no password here and nothing to send. The mirror is `drivers/DriverSheet.tsx`,
 * which reserves a driver's profile the same way.
 *
 * `.is('admin_id', null)` IS NOT OPTIONAL ON THE UPDATE, for the reason DriverSheet spells out at
 * `:263-269`: `profiles_partner_id_key` is UNIQUE only WHERE `admin_id IS NULL`
 * (`baseline_schema.sql:9126`), so an admin holding a test profile against this same partner is
 * matched by `.eq('partner_id', …)` alone — and this update would overwrite that admin's own name,
 * phone and email with the partner's.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { captureError } from '@/lib/logger';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { CheckCircle2, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface PartnerLoginSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    partnerId: number;
    partnerName: string;
}

interface PartnerProfile {
    id: number;
    first_name: string;
    last_name: string;
    phone: string | null;
    email: string | null;
    user_id: string | null;
}

export default function PartnerLoginSheet({
    open,
    onOpenChange,
    partnerId,
    partnerName,
}: PartnerLoginSheetProps) {
    const queryClient = useQueryClient();
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');

    const { data: profile, isLoading, isError: profileFailed } = useQuery({
        queryKey: ['partner-login', partnerId],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('profiles')
                .select('id, first_name, last_name, phone, email, user_id')
                .eq('partner_id', partnerId)
                // The partner's own row, never an admin's test profile against the same partner.
                .is('admin_id', null)
                .maybeSingle();
            if (error) throw error;
            return (data as PartnerProfile | null) ?? null;
        },
        enabled: open && !!partnerId,
    });

    useEffect(() => {
        if (!open) return;
        setFirstName(profile?.first_name ?? '');
        setLastName(profile?.last_name ?? '');
        setPhone(profile?.phone ?? '');
        setEmail(profile?.email ?? '');
    }, [open, profile]);

    const saveMutation = useMutation({
        mutationFn: async () => {
            const fields = {
                first_name: firstName.trim(),
                last_name: lastName.trim(),
                phone: phone.trim() || null,
                email: email.trim() || null,
            };

            if (profile) {
                const { error } = await supabase
                    .from('profiles')
                    .update(fields)
                    .eq('partner_id', partnerId)
                    .is('admin_id', null);
                if (error) throw error;
                return;
            }

            const { error } = await supabase
                .from('profiles')
                .insert({ user_id: null, partner_id: partnerId, ...fields });
            if (error) throw error;
        },
        onSuccess: () => {
            toast.success(profile ? 'Partner login updated' : 'Partner login created');
            queryClient.invalidateQueries({ queryKey: ['partner-login', partnerId] });
            onOpenChange(false);
        },
        onError: (e: unknown) => {
            captureError(e);
            // The same two 23505s `drivers/DriverSheet.tsx:243-244` names: `profiles` is UNIQUE on
            // both phone and email, and a partner reusing a driver's number is the ordinary way in.
            const failure = e as { code?: string; message?: string } | null;
            if (failure?.code === '23505' && failure.message?.includes('phone')) toast.error('Phone number already in use');
            else if (failure?.code === '23505' && failure.message?.includes('email')) toast.error('Email already in use');
            // AND THE THIRD ONE, WHICH NAMES NEITHER COLUMN. `profiles_partner_id_key` is UNIQUE
            // `(partner_id) WHERE admin_id IS NULL`, so an INSERT attempted while a login already
            // exists — the state a failed read leaves this sheet drawing — lands here and used to
            // report "Failed to create" over a login that is sitting in the database.
            else if (failure?.code === '23505' && failure.message?.includes('partner_id')) {
                toast.error('This partner already has a login. Close the sheet and reopen it to edit it.');
            }
            else toast.error(profile ? 'Failed to update the partner login' : 'Failed to create the partner login');
        },
    });

    const canSave = !!firstName.trim() && !!lastName.trim() && !!phone.trim() && !saveMutation.isPending;

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="!inset-y-4 !right-4 !h-auto !w-[90vw] sm:!w-[500px] !max-w-none rounded-2xl border shadow-2xl bg-background p-6 flex flex-col gap-4"
            >
                <div className="space-y-1">
                    <SheetTitle className="text-2xl font-bold">Partner Login</SheetTitle>
                    <p className="text-sm text-muted-foreground">
                        Who signs in to the partner app for {partnerName}. They enter this phone number
                        and the code binds the account on first use.
                    </p>
                </div>

                {isLoading ? (
                    <div className="space-y-3">
                        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
                    </div>
                ) : profileFailed ? (
                    /* A FAILED READ IS NOT AN EMPTY ONE. With `profile` undefined the form below
                       draws as a CREATE — blank fields, a "Create login" button — and the insert it
                       offers violates `profiles_partner_id_key` whenever a login already exists.
                       The read has to say it failed rather than let the form imply an answer. */
                    <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm">
                        <p className="font-medium">The partner’s login could not be read</p>
                        <p className="mt-1 text-muted-foreground">
                            Whether this partner already has one is unknown, so nothing is offered here.
                            Close the sheet and open it again.
                        </p>
                    </div>
                ) : (
                    <>
                        {profile && (
                            <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-sm">
                                {profile.user_id ? (
                                    <>
                                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                                        <span>Signed in at least once.</span>
                                    </>
                                ) : (
                                    <>
                                        <Clock className="h-4 w-4 text-muted-foreground" />
                                        <span>Waiting on their first sign-in.</span>
                                    </>
                                )}
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label htmlFor="partner-login-first">First name *</Label>
                                <Input
                                    id="partner-login-first"
                                    autoComplete="off"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    autoFocus
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="partner-login-last">Last name *</Label>
                                <Input
                                    id="partner-login-last"
                                    autoComplete="off"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="partner-login-phone">Phone *</Label>
                            <Input
                                id="partner-login-phone"
                                type="tel"
                                autoComplete="off"
                                inputMode="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="e.g. 0244 123 456"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="partner-login-email">Email</Label>
                            <Input
                                id="partner-login-email"
                                type="email"
                                autoComplete="off"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Optional"
                            />
                        </div>

                        <div className="flex justify-end gap-2 border-t pt-4">
                            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                            <Button onClick={() => saveMutation.mutate()} disabled={!canSave}>
                                {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {profile ? 'Save changes' : 'Create login'}
                            </Button>
                        </div>
                    </>
                )}
            </SheetContent>
        </Sheet>
    );
}
